import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { usePermissions } from '../../user/permissions';
import { MenuOrga } from '../routing/sidebarMenu';
import { useHistory, useLocation } from 'react-router-dom';

import { ViewPort, MenuContainer } from '@weezevent/nacre';
import TicketLogo from '../../nacre/images/weezticket.svg';
import ErrorBoundary from '../errorBoundary';
import SideBarMenu from './sidebarMenu'; // eslint-disable-line import/no-named-as-default
import TopBarModules from './topBarModules/topBarModules';
import Loading from '../loading';

export const ContextTemplate = React.createContext({
    capacities: []
});

export default function Template({
    organization,
    event,
    children,
    partner,
    chart,
    webservice,
    ticket_office,
    syncTicketOffice,
    personal_space,
    product,
    moderationFlowId,
    ticket_shop,
    membership
}) {
    const permissions = usePermissions();
    const history = useHistory();
    const location = useLocation();
    const capacities = React.useMemo(() => {
        if (!organization) {
            return [];
        }
        let capacities = permissions.capacityOrga(organization.id);

        if (moderationFlowId) {
            capacities = [...permissions.capacityFlow(organization.id, moderationFlowId), ...capacities];
        }
        return Array.from(capacities);
    }, [organization, permissions]);

    const backHomeUrl = React.useMemo(() => {
        if (!organization) {
            return null;
        }

        // Case Level Orga
        if (!event && !partner && !ticket_office && !chart && !membership && !personal_space) {
            return `/ticket/O${organization.id}`;
        }

        if (event) {
            return `/ticket/O${organization.id}/E${event.id}/dashboard`;
        }

        if (partner) {
            return `/ticket/O${organization.id}/partners/${partner.uuid}/dashboard`;
        }

        if (ticket_office) {
            return `/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/dashboard`;
        }

        if (chart) {
            return `/ticket/O${organization.id}/charts/${chart.uuid}/dashboard`;
        }

        if (membership) {
            return `/ticket/O${organization.id}/memberships/${membership.id}/members`;
        }
        if (webservice) {
            return `/ticket/O${organization.id}/webservices/${webservice.id}/dashboard`;
        }

        if (personal_space) {
            return `/ticket/O${organization.id}/personal_spaces/${personal_space.id}/settings`;
        }

        if (product) {
            return `/ticket/O${organization.id}/products/${product.id}/settings`;
        }

        if (ticket_shop) {
            return `/ticket/O${organization.id}/ticket_shop/${ticket_shop.id}/settings`;
        }
    }, [organization, event, partner, chart, membership, webservice, ticket_office, personal_space, product]);

    const hasOptionalMenu = React.useMemo(() => {
        if (!event && !partner && !ticket_office && !chart && !membership && !webservice && !moderationFlowId && !personal_space && !ticket_shop && !product) {
            return null;
        }
        return {
            menu: true,
            isLevelEvent: Boolean(!partner && !ticket_office && !chart && !membership && !webservice && !moderationFlowId && !personal_space)
        };
    }, [event, partner, ticket_office, chart, membership, webservice, moderationFlowId, personal_space]);

    return (
        <ContextTemplate.Provider value={{ capacities }}>
            <ViewPort
                logo={TicketLogo}
                onClickLogo={organization ? () => history.push(`/ticket/O${organization.pk}`) : null}
                topBarModules={<TopBarModules organization={organization} />}
                displayMenuButton={Boolean(organization)}
            >
                <MenuContainer
                    display={Boolean(organization)}
                    optionalMenu={hasOptionalMenu?.menu && <MenuOrga organization={organization} islevelevent={hasOptionalMenu?.islevelevent} />}
                    menu={
                        <SideBarMenu
                            organization={organization}
                            event={event}
                            partner={partner}
                            ticket_office={ticket_office}
                            syncTicketOffice={syncTicketOffice}
                            chart={chart}
                            webservice={webservice}
                            moderationFlowId={moderationFlowId}
                            personal_space={personal_space}
                            product={product}
                            ticket_shop={ticket_shop}
                            membership={membership}
                        />
                    }
                >
                    <ErrorBoundary location={location} organization={organization} backHomeUrl={backHomeUrl}>
                        <Suspense fallback={<Loading />}>{children}</Suspense>
                    </ErrorBoundary>
                </MenuContainer>
            </ViewPort>
        </ContextTemplate.Provider>
    );
}

Template.defaultProps = {
    event: null,
    organization: null,
    ticket_office: null,
    history: null,
    withLeftMenu: true,
    webservice: null,
    moderationFlowId: null,
    product: null,
    membership: null
};

Template.propTypes = {
    organization: PropTypes.object,
    event: PropTypes.object,
    ticket_office: PropTypes.object,
    chart: PropTypes.object,
    children: PropTypes.node,
    partner: PropTypes.object,
    product: PropTypes.object,
    webservice: PropTypes.object,
    moderationFlowId: PropTypes.string,
    membership: PropTypes.object
};
